import Axios from "axios";

import { Response } from "@konv/http";
import Bugsnag from "@bugsnag/js";
import { AndroidWebviewBridge } from "@/tools/AndroidWebviewBridge";
import { Logger } from "@/tools/Logger";

export class NetLogicAPI {
  constructor({ apiToken, baseUrl }) {
    this.apiToken = apiToken;
    this.baseUrl = baseUrl;

    this.client = Axios.create({
      baseURL: baseUrl,
      timeout: 1000 * 60 * 2,
      headers: {
        Authorization: `Bearer: ${this.apiToken}`,
      },
    });
  }

  async doRequest({ endpoint, method, headers, data, query, log = true }) {
    try {
      const queryParams = query ? { params: query } : {};

      const androidBridge = await AndroidWebviewBridge.getInstance();
      if (androidBridge.loaded) {
        const response = await androidBridge.request({
          url: [this.baseUrl, endpoint].join(""),
          method,
          headers: {
            ...headers,
            Authorization: `Bearer: ${this.apiToken}`,
          },
          data,
          query
        });

        if (log) {
          Logger.info('NetlogicAPI', {
            request: {
              url: endpoint,
              method,
              data,
              query,
            },
            response: {
              code: response.getCode(),
              data: response.getData(),
              errors: response.getErrors([]),
            },
          });
        }

        return response;
      }

      const response = await this.client.request({
        url: endpoint,
        method,
        data,
        headers: {
          ...headers,
        },
        ...queryParams,
      });

      Bugsnag.leaveBreadcrumb(
        "NetLogicAPI - Requisição",
        {
          url: endpoint,
          method,
          data,
          query,
          status: response?.status,
          response: response?.data,
        },
        "request"
      );

      return new Response(response.data, 200, []);
    } catch (err) {
      console.error('NetLogicAPI - Erro na requisição', err.message, err);

      if (log) {
        Logger.error('NetLogicAPI', {
          request: {
            url: endpoint,
            method,
            data,
            query,
          },
          response: {
            status: err?.response?.status,
            response: err?.response?.data,
          },
          error: {
            message: err.message,
            stack: err.stack,
          }
        });
      }

      Bugsnag.leaveBreadcrumb(
        "NetLogicAPI - Requisição",
        {
          url: endpoint,
          method,
          data,
          query,
          err: err?.message,
          status: err?.response?.status,
          response: err?.response?.data,
        },
        "request"
      );

      if (err.isAxiosError) {
        const response = err?.response?.data;

        if (!response) {
          return new Response(null, 1000, [`Falha na rede`]);
        }
  

        const errorMessageSegments = [
          response.errorCode,
          response.errorMessage,
          err.message,
        ];
        const errorMessage =
          `${errorMessageSegments.filter(Boolean).join(" - ")}` ||
          "Erro na comunicação com a NetLogic";

        return new Response(null, 1000, [errorMessage]);
      }      

      return new Response(null, 1000, [err.message]);
    }
  }

  async getProductAvailability(sku) {
    return await this.doRequest({
      endpoint: `/checkavailability`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {},
      query: {
        mimasterobjnum: sku,
      },
    });
  }

  async createOrder(order) {
    const response = await this.doRequest({
      endpoint: "/order",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: order,
    });

    return response;
  }

  async getOrderByCheckNumber({ checkNumber, employeeId }) {
    return await this.doRequest({
      endpoint: `/checkdetail`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        CheckNumber: checkNumber,
        EmployeeID: employeeId,
        Detail: true
      },
      data: {},
    });

  }

  async getOrderByTableNum({ tableNum, employeeId }) {
    return await this.doRequest({
      endpoint: `/checkdetail`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        TableNum: tableNum,
        EmployeeID: employeeId,
        Detail: true
      },
      data: {},
    });
  }

  async getCatalog() {
    const response = await this.doRequest({
      endpoint: "/menu",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {},
      log: false,
    });

    return response;
  }
}
